import { useCallback, useEffect, useRef, useState } from 'react';

// Dictation — voice → text for a SINGLE field, transcribed verbatim (the user's
// own words, no AI interpretation). Uses the Web Speech API where present
// (Chrome/Edge/Safari); `supported:false` elsewhere so callers can hide the
// control (OS keyboard dictation remains the universal fallback).
//
// interimResults is ON so partial words stream to `onInterim` the instant they
// are heard. `warm()` pre-creates the recognition instance (call it when the UI
// opens or on hover) and the instance is REUSED across start/stop, so repeat
// dictations skip construction. NOTE: the page's Permissions-Policy must allow
// `microphone=(self)`. The fastest, highest-quality path remains the OS's own
// dictation (macOS Dictation/SuperWhisper, Windows Win+H) typing into the field.

interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  0: RecognitionAlternative;
  isFinal: boolean;
}
interface RecognitionResultList {
  length: number;
  [index: number]: RecognitionResult;
}
interface RecognitionEvent {
  resultIndex: number;
  results: RecognitionResultList;
}
interface RecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function messageFor(code: string): string | null {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Allow microphone access to dictate.';
    case 'audio-capture':
      return 'No microphone was found.';
    case 'network':
      return 'Dictation needs a network connection.';
    case 'no-speech':
    case 'aborted':
      return null; // benign — a pause or a manual stop
    default:
      return 'Dictation stopped. Please try again.';
  }
}

export interface UseDictationOptions {
  lang?: string;
  /** Live partial transcript (streams as the person speaks). */
  onInterim?: (partial: string) => void;
}

export interface UseDictation {
  /** Web Speech API available in this browser. */
  supported: boolean;
  listening: boolean;
  /** Last error, in plain language (e.g. permission denied); null when fine. */
  error: string | null;
  /** Start if idle, stop if listening. */
  toggle: () => void;
  stop: () => void;
  /** Pre-create the recognition instance to cut first-use latency. */
  warm: () => void;
}

/** Stream FINAL transcript segments to `onFinal` (verbatim) and, if given, live
 *  partials to `opts.onInterim`. The consumer appends finals to its field. */
export function useDictation(
  onFinal: (segment: string) => void,
  opts?: UseDictationOptions,
): UseDictation {
  const lang = opts?.lang ?? 'en-NZ';
  const [supported] = useState<boolean>(() => getRecognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const onInterimRef = useRef(opts?.onInterim);
  onInterimRef.current = opts?.onInterim;

  const ensureRec = useCallback((): SpeechRecognitionLike | null => {
    if (recRef.current) return recRef.current;
    const Ctor = getRecognitionCtor();
    if (!Ctor) return null;
    let rec: SpeechRecognitionLike;
    try {
      rec = new Ctor();
    } catch {
      return null;
    }
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const seg = result[0].transcript;
        if (result.isFinal) {
          const t = seg.trim();
          if (t) onFinalRef.current(t);
        } else {
          interim += seg;
        }
      }
      onInterimRef.current?.(interim.trim());
    };
    rec.onerror = (event) => {
      const message = messageFor(event.error);
      if (message) setError(message);
    };
    rec.onend = () => {
      setListening(false);
      onInterimRef.current?.('');
    };
    recRef.current = rec;
    return rec;
  }, [lang]);

  const warm = useCallback(() => {
    ensureRec();
  }, [ensureRec]);

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const toggle = useCallback(() => {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = ensureRec();
    if (!rec) {
      setError('Dictation is not supported in this browser.');
      return;
    }
    setError(null);
    setListening(true);
    try {
      rec.start();
    } catch {
      setListening(false);
      setError('Dictation could not start. Please try again.');
    }
  }, [listening, ensureRec]);

  useEffect(
    () => () => {
      recRef.current?.stop();
      recRef.current = null;
    },
    [],
  );

  return { supported, listening, error, toggle, stop, warm };
}
